// Phase 2: Advanced Multi-Criteria Filter Panel Component
import React, { useCallback, useTransition } from 'react';
import clsx from 'clsx';
import styles from './FilterPanel.module.css';
import type { VettingStatus } from '../types/vetting';

// Award Program Eligibility & Requirements Filters
export type BasicFilters = {
  // Program Requirements
  isbn_required?: 'yes' | 'no' | 'all';
  independent_publishers_allowed?: 'yes' | 'no' | 'all';

  // Award Structure
  entry_fees?: 'free' | 'paid' | 'all';
  multiple_prizes_offered?: 'yes' | 'no' | 'all'; // Gold/Silver/Bronze vs single prize

  // Vetting System
  vetting_statuses?: VettingStatus[];
  
  // Priority filtering
  priority_only?: boolean;

  // Sorting (UI only)
  sort_by?: 'name' | 'categories';
  sort_direction?: 'asc' | 'desc';

  // Legacy fields for compatibility (will be removed)
  foundation_year_range?: { min?: number; max?: number };
};

interface FilterPanelProps {
  filters: BasicFilters;
  onFiltersChange: (filters: BasicFilters) => void;
  programCount?: number;
  categoryCount?: number;
  opportunityCount?: number;
  isLoading?: boolean;
  className?: string;
}

// Local helper to count active filters for summary display
const getActiveFilterCount = (filters: BasicFilters): number => {
  return Object.entries(filters)
    .filter(([key, value]) => {
      if (['sort_by', 'sort_direction'].includes(key)) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object') {
        return Object.values(value).some((v) => v !== undefined && v !== null);
      }
      if (typeof value === 'string') {
        if (value.trim().toLowerCase() === 'all') return false;
        return value.trim().length > 0;
      }
      return value !== undefined && value !== null;
    }).length;
};

const VETTING_STATUS_ORDER: VettingStatus[] = ['green', 'yellow', 'red'];
const VETTING_LABELS: Record<VettingStatus, string> = {
  green: 'Approved',
  yellow: 'Review',
  red: 'Not suitable'
};

const ENTRY_FEE_OPTIONS = [
  { value: 'all', label: 'All Programs' },
  { value: 'free', label: 'Free Only' },
  { value: 'paid', label: 'Paid Entry' }
] as const;

const BOOLEAN_FILTERS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  programCount,
  categoryCount,
  opportunityCount,
  isLoading = false,
  className = ''
}) => {
  const [collapsed, setCollapsed] = React.useState(true);
  const [, startTransition] = useTransition();
  const activeFilterCount = getActiveFilterCount(filters);
  const vettingSelectedSet = React.useMemo(() => new Set(filters.vetting_statuses ?? []), [filters.vetting_statuses]);
  const vettingAllSelected = !filters.vetting_statuses || filters.vetting_statuses.length === 0;

  const updateFilters = useCallback((updates: Partial<BasicFilters>) => {
    const hasChanges = Object.entries(updates).some(([key, value]) => {
      const typedKey = key as keyof BasicFilters;
      return !Object.is(filters[typedKey], value);
    });

    if (!hasChanges) {
      return;
    }

    startTransition(() => {
      onFiltersChange({ ...filters, ...updates });
    });
  }, [filters, onFiltersChange, startTransition]);

  const renderBooleanButtons = (
    options: Array<{ value: string; label: string }>,
    current: string | undefined,
    onSelect: (value: string) => void
  ) => (
    <div className={styles.buttonRow}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={clsx(
            styles.chipButton,
            (current || 'all') === option.value && styles.chipButtonActive
          )}
          onClick={() => onSelect(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className={clsx(styles.root, className)}>
      <div className={styles.header}>
        <div className={styles.title}>
          🔍 Filters
          <button
            type="button"
            className={styles.toggleButton}
            onClick={() => setCollapsed((prev) => !prev)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Show filters' : 'Hide filters'}
          >
            {collapsed ? 'Show ▼' : 'Hide ▲'}
          </button>
        </div>
        {programCount !== undefined && (
          <span className={styles.statusText}>
            {isLoading ? '⏳ Loading…' : (
              <>
                {programCount} program{programCount !== 1 ? 's' : ''}
                {categoryCount !== undefined && categoryCount > 0 && (
                  <> • {categoryCount} categor{categoryCount !== 1 ? 'ies' : 'y'}</>
                )}
                {opportunityCount !== undefined && opportunityCount > 0 && (
                  <> • {opportunityCount} opportunit{opportunityCount !== 1 ? 'ies' : 'y'}</>
                )}
              </>
            )}
          </span>
        )}
      </div>

      {!collapsed && (
        <div className={styles.grid}>
          <div className={styles.group}>
            <label className={styles.label}>ISBN Required</label>
            {renderBooleanButtons(
              BOOLEAN_FILTERS,
              filters.isbn_required,
              (value) => updateFilters({ isbn_required: value as BasicFilters['isbn_required'] })
            )}
          </div>

          <div className={styles.group}>
            <label className={styles.label}>Independent Publishers Allowed</label>
            {renderBooleanButtons(
              BOOLEAN_FILTERS,
              filters.independent_publishers_allowed,
              (value) =>
                updateFilters({ independent_publishers_allowed: value as BasicFilters['independent_publishers_allowed'] })
            )}
          </div>

          <div className={styles.group}>
            <label className={styles.label}>Entry Fees</label>
            <div className={styles.buttonRow}>
              {ENTRY_FEE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={clsx(
                    styles.chipButton,
                    (filters.entry_fees || 'all') === option.value && styles.chipButtonActive
                  )}
                  onClick={() => updateFilters({ entry_fees: option.value as BasicFilters['entry_fees'] })}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.group}>
            <label className={styles.label}>Multiple Prizes Offered</label>
            {renderBooleanButtons(
              BOOLEAN_FILTERS,
              filters.multiple_prizes_offered,
              (value) =>
                updateFilters({ multiple_prizes_offered: value as BasicFilters['multiple_prizes_offered'] })
            )}
          </div>

          <div className={styles.group}>
            <label className={styles.label}>Vetting Status</label>
            <div className={styles.buttonRow}>
              <button
                type="button"
                className={clsx(
                  styles.chipButton,
                  styles.vettingButton,
                  vettingAllSelected && styles.vettingButtonAllActive
                )}
                onClick={() => updateFilters({ vetting_statuses: undefined })}
              >
                All
              </button>
              {VETTING_STATUS_ORDER.map((status) => {
                const isActive = vettingSelectedSet.has(status);
                return (
                  <button
                    key={status}
                    type="button"
                    className={clsx(
                      styles.chipButton,
                      styles.vettingButton,
                      status === 'green' && isActive && styles.vettingButtonApprovedActive,
                      status === 'yellow' && isActive && styles.vettingButtonReviewActive,
                      status === 'red' && isActive && styles.vettingButtonCriticalActive
                    )}
                    onClick={() => {
                      const next = new Set(filters.vetting_statuses ?? []);
                      if (next.has(status)) {
                        if (next.size === 1) {
                          return;
                        }
                        next.delete(status);
                      } else {
                        next.add(status);
                      }
                      const ordered = VETTING_STATUS_ORDER.filter((item) => next.has(item));
                      updateFilters({
                        vetting_statuses:
                          ordered.length === VETTING_STATUS_ORDER.length ? undefined : ordered
                      });
                    }}
                  >
                    {VETTING_LABELS[status]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <div className={styles.summary}>
          {activeFilterCount > 0
            ? `${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active`
            : 'No filters applied'}
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
